import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LifeBuoy, Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';

interface SupportFeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

type FeedbackType = 'bug' | 'idea' | 'question' | 'support';

const TYPES: { key: FeedbackType; label: string; placeholder: string }[] = [
  {
    key: 'bug',
    label: 'Bug',
    placeholder: "What's broken? Steps to reproduce help us a lot.",
  },
  {
    key: 'idea',
    label: 'Idea',
    placeholder: 'What would you like to see?',
  },
  {
    key: 'question',
    label: 'Question',
    placeholder: "What's your question?",
  },
  {
    key: 'support',
    label: 'Support',
    placeholder: 'What do you need help with?',
  },
];

export function SupportFeedbackModal({ open, onClose }: SupportFeedbackModalProps) {
  const [type, setType] = useState<FeedbackType>('bug');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const placeholder =
    TYPES.find((t) => t.key === type)?.placeholder || 'Tell us more…';

  // Reset state when the modal opens.
  useEffect(() => {
    if (open) {
      setType('bug');
      setMessage('');
      setError(null);
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  // Esc-to-close.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Cleanup the auto-close timer.
  useEffect(() => {
    return () => {
      if (autoCloseRef.current) {
        clearTimeout(autoCloseRef.current);
        autoCloseRef.current = null;
      }
    };
  }, []);

  async function handleSend() {
    const trimmed = message.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/api/feedback', { message: trimmed, type });
      setSuccess(true);
      // Auto-close after ~2.5s.
      autoCloseRef.current = setTimeout(() => {
        onClose();
      }, 2500);
    } catch (err: any) {
      const msg =
        (err && (err.message || err.body?.error)) ||
        "Couldn't send right now — please try again.";
      setError(typeof msg === 'string' ? msg : "Couldn't send right now — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleSendAnother() {
    if (autoCloseRef.current) {
      clearTimeout(autoCloseRef.current);
      autoCloseRef.current = null;
    }
    setSuccess(false);
    setMessage('');
    setError(null);
    setType('bug');
  }

  const canSubmit = message.trim().length > 0 && !submitting;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-bg/70 backdrop-blur-sm"
            aria-hidden="true"
          />
          {/* Centering wrapper — framer-motion animates transform on the modal,
              which clobbers any Tailwind translate utilities. So we let a
              non-animated parent handle the centering via flex. */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Support & Feedback"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'w-full sm:w-[480px] max-h-[80vh] flex flex-col pointer-events-auto',
              'bg-bg-elevated border border-border rounded-2xl shadow-elevation',
            )}
          >
            <header className="px-5 py-4 border-b border-border/70 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <span className="inline-flex items-center justify-center size-9 rounded-lg bg-accent/15 text-accent shrink-0">
                  <LifeBuoy className="size-4" />
                </span>
                <div className="min-w-0">
                  <h2 className="text-base font-bold text-fg leading-tight">
                    Support &amp; Feedback
                  </h2>
                  <p className="text-2xs text-fg-subtle mt-1 leading-snug">
                    Tell us what's working, what's broken, or ask for help — we read everything.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="shrink-0 p-1 -mr-1 rounded text-fg-muted hover:text-fg hover:bg-bg-card transition-colors"
              >
                <X className="size-4" />
              </button>
            </header>

            {success ? (
              <div className="flex-1 flex flex-col items-center justify-center px-5 py-10 text-center">
                <span className="inline-flex items-center justify-center size-12 rounded-full bg-bull/15 text-bull">
                  <Check className="size-6" />
                </span>
                <p className="mt-4 text-sm font-semibold text-fg">
                  Thanks — we got it.
                </p>
                <button
                  type="button"
                  onClick={handleSendAnother}
                  className="mt-3 text-2xs font-semibold text-accent hover:underline focus:outline-none focus-visible:underline"
                >
                  Send another
                </button>
                <div className="mt-5">
                  <Button variant="secondary" size="sm" onClick={onClose}>
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
                  {/* Type pills */}
                  <div className="space-y-1.5">
                    <label className="block text-2xs font-semibold uppercase tracking-wide text-fg-subtle">
                      Type
                    </label>
                    <div className="inline-flex items-center gap-0.5 rounded-lg bg-bg-elevated p-0.5 border border-border/70">
                      {TYPES.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setType(t.key)}
                          aria-pressed={type === t.key}
                          className={cn(
                            'rounded-md px-3 py-1.5 text-2xs font-semibold transition-colors',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
                            type === t.key
                              ? 'bg-accent/15 text-accent'
                              : 'text-fg-muted hover:text-fg',
                          )}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5">
                    <label
                      htmlFor="feedback-message"
                      className="block text-2xs font-semibold uppercase tracking-wide text-fg-subtle"
                    >
                      Message
                    </label>
                    <textarea
                      id="feedback-message"
                      rows={5}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={placeholder}
                      disabled={submitting}
                      className={cn(
                        'block w-full rounded-lg px-3.5 py-2.5 text-sm leading-relaxed resize-none',
                        'bg-bg-card border border-border text-fg placeholder:text-fg-subtle',
                        'transition-colors duration-150',
                        'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30',
                        'disabled:opacity-60 disabled:cursor-not-allowed',
                      )}
                    />
                    {error && (
                      <p className="text-2xs text-bear" role="alert">
                        {error}
                      </p>
                    )}
                  </div>
                </div>

                <footer className="px-5 py-3 border-t border-border/70 flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={!canSubmit}
                    onClick={handleSend}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="size-3.5 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>Send</>
                    )}
                  </Button>
                </footer>
              </>
            )}
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
